import { observer } from 'mobx-react';
import { Fragment, ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import routes from '../../routes/index.routes';
import { IAdminxStore } from '../../stores';
import { IRequestInterceptor, IResponseInterceptor, ask } from '@monorepo/tools/src/lib/tools/ask/ask';
import { useUser } from '../../hooks/apis/use-user';
import { BarLoader } from '@monorepo/base/src/components/bar-loader/bar-loader';
import LogRocket from 'logrocket';

interface IRequireAuth {
	children: ReactNode;
}

/**
 * Redirect users to login page in case of user not authenticated
 */
// export const RequireAuth = observer((props: IRequireAuth) => {
// 	const { children } = props;
// 	const { authStore } = useStores<IAdminxStore>();
// 	const location = useLocation();

// 	// TODO - add here user roles
// 	if (!authStore.getToken()) {
// 		// Redirect them to the /login page, but save the current location they were
// 		// trying to go to when they were redirected. This allows us to send them
// 		// along to that page after they login, which is a nicer user experience
// 		// than dropping them off on the home page.
// 		return <Navigate to={routes.auth.login()} state={{ from: location }} replace />;
// 	}

// 	return <Fragment>{children}</Fragment>; // The fragment is just for not typescript yelling
// });

export const RequireAuth = observer((props: IRequireAuth) => {
	const { children } = props;
	const { authStore, userStore } = useStores<IAdminxStore>();
	const token = authStore.getToken();
	const location = useLocation();
	const user = userStore.getUser(); // not permissions

	// Must be before requests
	const authInterceptor: IRequestInterceptor = {
		call: (url, requestOptions) => {
			if (token) {
				requestOptions.headers = {
					Authorization: `Bearer ${token}`,
					...requestOptions.headers,
				};
			}
		},
	};
	ask.interceptors.requests.set('Auth', authInterceptor);
	const updateTokenInterceptor: IResponseInterceptor = {
		success: response => {
			const userToken = response.headers.get('User-Token');
			if (!userToken) {
				return null;
			}
			authStore.setToken(userToken);
		},
	};
	ask.interceptors.responses.set('UpdateToken', updateTokenInterceptor);

	const { isLoading: isUserLoading, isSuccess: isUserSuccess, error: userError } = useUser();

	useEffect(() => {
		if (isUserLoading || !isUserSuccess || !user?.id) {
			return;
		}

		LogRocket.identify(String(user.id), {
			name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
			email: user.email,
		});
	}, [isUserSuccess]);

	const authToken = authStore.getToken();
	if (!authToken || !user?.id || !user?.isVerified()) {
		if (authToken) {
			LogRocket.startNewSession();
		}
		userStore.removeUser();
		authStore.removeToken(false, { authToken, user });
		// Redirect them to the /login page, but save the current location they were
		// trying to go to when they were redirected. This allows us to send them
		// along to that page after they login, which is a nicer user experience
		// than dropping them off on the home page.
		return <Navigate to={routes.auth.login()} state={{ from: location }} replace />;
	}

	if (!isUserLoading && isUserSuccess) {
		if (!user?.getDomainPermissions()) {
			user?.setDomainPermissions({});
			return <Navigate to={routes.general.home()} state={{ from: location }} replace />;
		}
		return <Fragment>{children}</Fragment>; // The fragment is just for not typescript yelling
	}

	if (userError) {
		LogRocket.startNewSession();
		userStore.removeUser();
		authStore.removeToken(false, { userError });
		return <Navigate to={routes.auth.login()} state={{ from: location }} replace />;
	}

	return <BarLoader is={true} />;
});
